import { useRef, useEffect } from 'react'
import { gsap } from 'gsap'
import './Button.css'

interface ButtonProps {
  text: string
  onClick: () => void
  variant?: 'primary' | 'confirm' | 'secondary' | 'cancel' | 'reset'
  type?: 'button' | 'submit' | 'reset'
  className?: string
  title?: string
  disabled?: boolean
}

function Button({ 
  text, 
  onClick, 
  variant = 'primary', 
  type = 'button',
  className = '',
  title,
  disabled = false
}: ButtonProps) {
  const buttonRef = useRef<HTMLButtonElement>(null)
  const particlesRef = useRef<HTMLDivElement>(null)

  const createParticles = () => {
    if (!buttonRef.current || !particlesRef.current) return

    const particleContainer = particlesRef.current
    const particleCount = 8

    // Clear existing particles
    particleContainer.innerHTML = ''

    // Create particles
    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div')
      particle.className = 'particle'
      particleContainer.appendChild(particle)

      // Position at button center
      gsap.set(particle, {
        position: 'absolute',
        left: '50%',
        top: '50%',
        width: 4,
        height: 4,
        borderRadius: '50%',
        backgroundColor: variant === 'reset' 
          ? '#4caf50'
          : 'var(--accent-color)',
        pointerEvents: 'none',
        zIndex: 1000
      })

      // Calculate random direction
      const angle = (i / particleCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.5
      const distance = 30 + Math.random() * 20
      const endX = Math.cos(angle) * distance
      const endY = Math.sin(angle) * distance

      // Animate particle
      gsap.timeline()
        .to(particle, {
          x: endX,
          y: endY,
          scale: Math.random() * 0.5 + 0.5,
          opacity: 1,
          duration: 0.3,
          ease: 'power2.out'
        })
        .to(particle, {
          opacity: 0,
          scale: 0,
          duration: 0.3,
          ease: 'power2.in',
          onComplete: () => {
            if (particle.parentNode) {
              particle.parentNode.removeChild(particle)
            }
          }
        })
    }
  }

  const handleClick = () => {
    if (variant === 'reset') {
      createParticles()
    }
    onClick()
  }

  useEffect(() => {
    const currentParticlesRef = particlesRef.current
    
    // Cleanup function
    return () => {
      if (currentParticlesRef) {
        currentParticlesRef.innerHTML = ''
      }
    }
  }, [])

  const baseClass = 'btn'
  const variantClass = `btn-${variant}`
  const fullClassName = `${baseClass} ${variantClass} ${className}`.trim()

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        type={type}
        onClick={handleClick}
        className={fullClassName}
        title={title}
        disabled={disabled}
      >
        {text}
      </button>
      <div 
        ref={particlesRef}
        className="particle-container"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          overflow: 'visible'
        }}
      />
    </div>
  )
}

export default Button
